import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../../WebDevelopmentPage/components/AnimatedSection";
import { oesModules, ModuleFeature } from "../oesModulesData";
import { oesFeatureIconMap } from "../oesModuleIcons";

const FeatureBlock: React.FC<{ feature: ModuleFeature }> = ({ feature }) => {
  const Icon = oesFeatureIconMap[feature.icon];

  return (
    <div className="hms-module-feature oes-module-feature">
      <div className="oes-module-feature__head">
        <span className="oes-module-feature__icon" aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <h4 className="hms-module-feature__title oes-module-feature__title">{feature.title}</h4>
      </div>
      {feature.description && <p className="hms-module-feature__desc">{feature.description}</p>}
      {feature.items && (
        <ul className="hms-module-feature__list">
          {feature.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const tabLabels: Record<string, string> = {
  admin: "Admin",
  "question-bank": "Question Bank",
  "exam-engine": "Exam Engine",
  proctoring: "Proctoring",
  student: "Student",
  evaluation: "Evaluation",
};

const OesModulesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(oesModules[0].id);
  const activeModule = oesModules.find((m) => m.id === activeId) ?? oesModules[0];

  return (
    <section className="hms-modules" aria-labelledby="oes-modules-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2
            id="oes-modules-title"
            className="wdev-section-head__title wdev-section-head__title--dark wdev-section-head__title--underline"
          >
            Platform Modules
          </h2>
          <p className="hms-modules__lead">
            Six integrated modules covering administration, question management, exam delivery,
            proctoring, student access, and result processing.
          </p>
        </AnimatedSection>

        <div className="hms-modules__tabs oes-modules__tabs" role="tablist" aria-label="Online exam modules">
          {oesModules.map((mod) => (
            <button
              key={mod.id}
              type="button"
              role="tab"
              aria-selected={activeId === mod.id}
              aria-controls={`oes-panel-${mod.id}`}
              id={`oes-tab-${mod.id}`}
              className={`hms-modules__tab ${activeId === mod.id ? "hms-modules__tab--active" : ""}`}
              onClick={() => setActiveId(mod.id)}
            >
              {tabLabels[mod.id] ?? mod.title}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id}
            id={`oes-panel-${activeModule.id}`}
            role="tabpanel"
            aria-labelledby={`oes-tab-${activeModule.id}`}
            className="hms-modules__panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="hms-modules__panel-head">
              <h3>{activeModule.title}</h3>
              <p>{activeModule.description}</p>
            </div>

            <div className="hms-modules__features">
              {activeModule.features.map((feature) => (
                <FeatureBlock key={feature.title} feature={feature} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default OesModulesSection;
